export type PasswordSignInStep = 'credentials' | 'device_trust';

type SignInStatusLike =
  | 'needs_identifier'
  | 'needs_first_factor'
  | 'needs_second_factor'
  | 'needs_client_trust'
  | 'needs_new_password'
  | 'complete'
  | null
  | undefined;

type SecondFactorLike = ReadonlyArray<{ strategy: string }> | null | undefined;

function offersEmailCode(supportedSecondFactors: SecondFactorLike): boolean {
  return Boolean(supportedSecondFactors?.some((factor) => factor.strategy === 'email_code'));
}

/**
 * Which screen the card shows after a correct password.
 *
 * Clerk's new-device check (Client Trust) asks for an emailed code in one of
 * two shapes: `needs_client_trust`, or `needs_second_factor` with an
 * `email_code` factor — Clerk's own `<SignIn>` renders both as the same code
 * screen. Production has no instance-level second factors, so in practice an
 * email-code second factor is this check; treating it as anything else left
 * the card saying "This account requires an extra verification step" with no
 * way to receive the code.
 */
export function resolvePasswordSignInStep(
  status: SignInStatusLike,
  supportedSecondFactors?: SecondFactorLike,
): PasswordSignInStep {
  if (status === 'needs_client_trust') return 'device_trust';
  if (status === 'needs_second_factor' && offersEmailCode(supportedSecondFactors)) {
    return 'device_trust';
  }
  return 'credentials';
}

type DeviceTrustSignIn = {
  status: string;
  supportedSecondFactors?: ReadonlyArray<{ strategy: string }> | null;
  mfa: {
    sendEmailCode: () => Promise<{ error: unknown }>;
  };
};

/**
 * After a successful password attempt, send the new-device email code when required.
 */
export async function prepareDeviceTrustVerification(signIn: DeviceTrustSignIn): Promise<void> {
  if (
    resolvePasswordSignInStep(signIn.status as SignInStatusLike, signIn.supportedSecondFactors) !==
    'device_trust'
  ) {
    return;
  }
  if (!offersEmailCode(signIn.supportedSecondFactors)) {
    return;
  }

  await signIn.mfa.sendEmailCode();
}
